import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faVideo, faCheck, faClock, faBan, faTrash,
  faPlay, faUser, faEnvelope, faPhone, faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import {
  getAdminTestimonials,
  updateTestimonialStatus,
  deleteTestimonial,
} from "../api/admin";
import "./TestimonialsManagement.css";

const STATUS_MAP = {
  pending:  { label: "Pendente",  cls: "pending",  icon: faClock },
  approved: { label: "Aprovado",  cls: "approved", icon: faCheck },
  rejected: { label: "Rejeitado", cls: "rejected", icon: faBan },
};

const PAGE_SIZE = 20;

const TestimonialsManagement = () => {
  const [filter, setFilter]           = useState("");
  const [rows, setRows]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [loading, setLoading]         = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [previewUrl, setPreviewUrl]   = useState(null);
  const [noteModal, setNoteModal]     = useState(null);
  const [noteText, setNoteText]       = useState("");

  const load = useCallback(async (status = filter, p = page) => {
    setLoading(true);
    try {
      const data = await getAdminTestimonials(status, p * PAGE_SIZE, PAGE_SIZE);
      setRows(data.testimonials || []);
      setTotal(data.total || 0);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (v) => {
    setFilter(v);
    setPage(0);
    load(v, 0);
  };

  const handleStatus = async (id, status) => {
    setActionLoading(id + status);
    try {
      await updateTestimonialStatus(id, status, noteText);
      setNoteModal(null);
      setNoteText("");
      load();
    } catch {
      // silencioso
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este depoimento permanentemente?")) return;
    setActionLoading(id + "del");
    try {
      await deleteTestimonial(id);
      load();
    } catch {
      // silencioso
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="tm-root">
      <div className="tm-header">
        <div className="tm-header-left">
          <FontAwesomeIcon icon={faVideo} className="tm-header-icon" />
          <div>
            <h2 className="tm-title">Depoimentos de Clientes</h2>
            <p className="tm-subtitle">{total} vídeo{total !== 1 ? "s" : ""} enviado{total !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <button className="tm-refresh-btn" onClick={() => load()} title="Atualizar">
          <FontAwesomeIcon icon={faRefresh} spin={loading} />
        </button>
      </div>

      {/* Filtros */}
      <div className="tm-filters">
        {["", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            className={`tm-filter-btn ${filter === s ? "active" : ""}`}
            onClick={() => handleFilter(s)}
          >
            {s === "" ? "Todos" : STATUS_MAP[s].label}
          </button>
        ))}
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="tm-loading"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>
      ) : rows.length === 0 ? (
        <div className="tm-empty">
          <FontAwesomeIcon icon={faVideo} className="tm-empty-icon" />
          <p>Nenhum depoimento {filter ? `com status "${STATUS_MAP[filter]?.label}"` : "enviado"} ainda.</p>
        </div>
      ) : (
        <div className="tm-list">
          {rows.map((r) => {
            const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
            const busy = actionLoading?.startsWith(r.id);
            return (
              <div key={r.id} className={`tm-card ${st.cls}`}>
                <div className="tm-card-top">
                  <div className="tm-card-user">
                    <div className="tm-avatar">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div className="tm-user-info">
                      <span className="tm-user-name">{r.user_name || "—"}</span>
                      <span className="tm-user-detail">
                        <FontAwesomeIcon icon={faEnvelope} /> {r.user_email || "—"}
                      </span>
                      {r.user_phone && (
                        <span className="tm-user-detail">
                          <FontAwesomeIcon icon={faPhone} /> {r.user_phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="tm-card-meta">
                    <span className={`tm-status-badge ${st.cls}`}>
                      <FontAwesomeIcon icon={st.icon} /> {st.label}
                    </span>
                    <span className="tm-date">
                      {new Date(r.created_at).toLocaleString("pt-BR")}
                    </span>
                    {r.reward_granted && (
                      <span className="tm-reward-badge">🎰 Giro creditado</span>
                    )}
                  </div>
                </div>

                {r.note && (
                  <p className="tm-note">📝 {r.note}</p>
                )}

                <div className="tm-card-actions">
                  <button
                    className="tm-action-btn preview"
                    onClick={() => setPreviewUrl(r.video_url)}
                    title="Assistir vídeo"
                  >
                    <FontAwesomeIcon icon={faPlay} /> Assistir
                  </button>

                  {r.status !== "approved" && (
                    <button
                      className="tm-action-btn approve"
                      disabled={busy}
                      onClick={() => handleStatus(r.id, "approved")}
                      title="Aprovar"
                    >
                      {busy && actionLoading === r.id + "approved"
                        ? <FontAwesomeIcon icon={faSpinner} spin />
                        : <FontAwesomeIcon icon={faCheck} />
                      } Aprovar
                    </button>
                  )}

                  {r.status !== "rejected" && (
                    <button
                      className="tm-action-btn reject"
                      disabled={busy}
                      onClick={() => { setNoteModal(r); setNoteText(""); }}
                      title="Rejeitar"
                    >
                      <FontAwesomeIcon icon={faBan} /> Rejeitar
                    </button>
                  )}

                  <button
                    className="tm-action-btn delete"
                    disabled={busy}
                    onClick={() => handleDelete(r.id)}
                    title="Excluir"
                  >
                    {busy && actionLoading === r.id + "del"
                      ? <FontAwesomeIcon icon={faSpinner} spin />
                      : <FontAwesomeIcon icon={faTrash} />
                    }
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="tm-pagination">
          <button disabled={page === 0} onClick={() => { setPage(p => p - 1); load(filter, page - 1); }}>‹ Anterior</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => { setPage(p => p + 1); load(filter, page + 1); }}>Próxima ›</button>
        </div>
      )}

      {/* Modal preview de vídeo */}
      {previewUrl && (
        <div className="tm-overlay" onClick={() => setPreviewUrl(null)}>
          <div className="tm-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tm-preview-close" onClick={() => setPreviewUrl(null)}>✕</button>
            <video src={previewUrl} controls autoPlay className="tm-preview-video" />
          </div>
        </div>
      )}

      {/* Modal de rejeição com nota */}
      {noteModal && (
        <div className="tm-overlay" onClick={() => setNoteModal(null)}>
          <div className="tm-note-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Rejeitar depoimento</h3>
            <p className="tm-note-modal-user">{noteModal.user_name}</p>
            <textarea
              className="tm-note-textarea"
              placeholder="Motivo (opcional)..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
            />
            <div className="tm-note-modal-actions">
              <button className="tm-action-btn reject" onClick={() => handleStatus(noteModal.id, "rejected")}>
                <FontAwesomeIcon icon={faBan} /> Confirmar Rejeição
              </button>
              <button className="tm-action-btn preview" onClick={() => setNoteModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
